'use client'

import { useEffect } from 'react'

import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'

import useStorage from '@/hooks/use-storage'
import { USER_AUTH_STORAGE_KEY } from '@/hooks/use-storage/use-storage.constants'
import { isLaunchDarklyEnabled, LAUNCHDARKLY_CLIENT_ID } from '@/lib/launchdarkly/constants'

interface LaunchDarklyProviderProps {
  children: React.ReactNode
}

const anonymousUser = {
  key: 'anonymous',
  anonymous: true,
}

function createLdUser(userAuth: UserAuth | null) {
  if (!userAuth?.user) {
    return anonymousUser
  }

  const { user } = userAuth
  return {
    key: user.id.toString(),
    email: user.email,
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined,
    anonymous: false,
    custom: {
      isSuperuser: user.isSuperuser,
      isStaff: user.isStaff,
    },
  }
}

// Inner component that updates user context when user changes
function LaunchDarklyContextUpdater({ children }: { children: React.ReactNode }) {
  const ldClient = useLDClient()
  const { value: userAuth, storageLoaded } = useStorage<UserAuth>(
    USER_AUTH_STORAGE_KEY,
    null,
    { subscribe: true }
  )

  useEffect(() => {
    if (!ldClient || !storageLoaded) {
      return
    }

    const ldUser = createLdUser(userAuth)
    ldClient.identify(ldUser)
  }, [ldClient, storageLoaded, userAuth])

  return <>{children}</>
}

export function LaunchDarklyProvider({ children }: LaunchDarklyProviderProps) {
  if (!isLaunchDarklyEnabled()) {
    return <>{children}</>
  }

  // Use synchronous LDProvider so the tree structure never changes after mount.
  // asyncWithLDProvider stored the component type in state and swapped it in after
  // initialization, which caused React to unmount and remount the entire child tree.
  return (
    <LDProvider
      clientSideID={LAUNCHDARKLY_CLIENT_ID}
      context={anonymousUser}
      options={{ bootstrap: 'localStorage' }}
      timeout={5}
    >
      <LaunchDarklyContextUpdater>{children}</LaunchDarklyContextUpdater>
    </LDProvider>
  )
}
